import { useState, useEffect } from 'react';
import JsonView from 'react18-json-view';
import { ArrowRightIcon } from 'lucide-react';
import { ITrainersData, ITrainersTool } from '@/types/trainers';
import ImageCropper from '@/components/ui/trainers/shared/ImageCropper';
import { IMapProvider, IPoints } from '@/types/map';
import DisplayMapFeatureCandidateContent from '@/components/ui/trainers/DisplayMapFeatureCandidateContent';
import { DisplayOnMap } from '@/components/ui/trainers/shared/DisplayOnMap';
import Markdown from '@/components/ui/trainers/shared/Markdown';
import ImageCarousel from '@/components/ui/trainers/ImageCarousel';
import { findIndexWithLargestDetectionArea } from '@/utils/trainers';

interface Props {
  data?: ITrainersData;
  tool?: ITrainersTool;
  contentHeight?: number;
}

export default function DefaultTrainerContent({
  data,
  tool,
  contentHeight,
}: Props) {
  const {
    images = [],
    detections,
    lat,
    lon,
    azimuth,
    positionCandidates,
    imagesCoords,
    elementDescription: label,
    prompt,
    response,
    validator,
    originalValue,
    expectedTarget,
  } = data?.content || {};
  const hasAppleMaps = !!tool?.args?.hasAppleMaps;
  const isAppleMapsDefault = tool?.args?.isAppleMapsDefault ?? false;
  const SVGOverlay = tool?.args?.svgOverlay;
  const [imageHeight, setImageHeight] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    if (detections && detections.length > 0) {
      const largestDetectionIndex =
        findIndexWithLargestDetectionArea(detections);
      if (largestDetectionIndex !== -1) {
        setSelectedImageIndex(largestDetectionIndex);
      } else {
        setSelectedImageIndex(0);
      }
    } else {
      setSelectedImageIndex(0);
    }
  }, [detections]);

  const image = images?.[selectedImageIndex] || '';
  const currentDetection = detections?.[selectedImageIndex];
  const shouldCropImage = tool?.args?.shouldCropImage ?? true;

  let options = {};
  let points = imagesCoords as IPoints;
  if (points) {
    options = {
      selectedCameraIndex: selectedImageIndex,
      alignViewBasedOnPoints: true,
      initialMapProvider: isAppleMapsDefault
        ? IMapProvider.Apple
        : IMapProvider.Mapbox,
    };
  }

  if (positionCandidates) {
    points = positionCandidates as IPoints;
    options = {
      hideTargetMarker: true,
      alignViewBasedOnPoints: true,
      selectedCameraIndex: undefined,
    };
  }

  function extractResponseToShow(response: Record<string, any>) {
    if (response.onRedRestricted !== undefined) {
      return (
        <div className="mt-2 whitespace-pre-wrap break-words text-left">
          <Markdown text={String(response.onRedRestricted)} />
        </div>
      );
    }

    if (response.suggestedLabel) {
      return (
        <div className="mt-2 whitespace-pre-wrap break-words text-left">
          <Markdown text={String(response.suggestedLabel)} />
        </div>
      );
    }
    if (response.suggestedSpeedType) {
      return (
        <div className="mt-2 whitespace-pre-wrap break-words text-left">
          <Markdown text={String(response.suggestedSpeedType)} />
        </div>
      );
    }
    if (response.suggestedTurnRule) {
      return (
        <div className="mt-2 whitespace-pre-wrap break-words text-left">
          <Markdown text={String(response.suggestedTurnRule)} />
        </div>
      );
    }
    if (response.suggestedSpeedLimit) {
      return (
        <div className="mt-2 whitespace-pre-wrap break-words text-left">
          <Markdown text={String(response.suggestedSpeedLimit)} />
        </div>
      );
    }
    return <JsonView src={response} collapseStringMode="address" />;
  }

  if (prompt && response) {
    return (
      <div className="mt-2 flex size-full flex-col items-center overflow-hidden">
        <div className="flex size-[62%] items-center justify-center overflow-hidden pb-10">
          <div className="max-h-full max-w-full">
            <ImageCropper
              key={image}
              limitWidth={240}
              limitHeight={280}
              imageSrc={image}
              boundingBox={currentDetection || []}
            />
          </div>
        </div>

        <div>
          {validator && (
            <div>
              <h2 className="text-left text-2xl font-bold">
                Validator: {validator}
              </h2>
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-row items-center">
          <h6 className="text-left font-bold">Outcome: &nbsp;</h6>
          {response.correct ? (
            <div>
              <h6 className="text-left font-bold text-veridianSuccess-600">
                AI marked as Correct
              </h6>
            </div>
          ) : (
            <div>
              <h6 className="text-left font-bold text-cinnabarDanger-600">
                AI marked as Incorrect
              </h6>
            </div>
          )}
        </div>

        {response.correct ? (
          <div>
            {originalValue && (
              <div className="flex flex-row overflow-auto p-10">
                <h3 className="text-left text-2xl font-bold">
                  Original Value: &nbsp;
                </h3>
                <div className="mt-2 whitespace-pre-wrap break-words text-left text-veridianSuccess-400">
                  <Markdown text={String(originalValue)} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid flex-1 grid-cols-3 items-center overflow-hidden">
            {originalValue ? (
              <>
                <div className="overflow-auto p-10">
                  <h3 className="text-left text-2xl font-bold">
                    Original Value
                  </h3>
                  <div className="mt-2 whitespace-pre-wrap break-words text-left text-cinnabarDanger-700">
                    <Markdown text={String(originalValue)} />
                  </div>
                </div>
                <div className="flex h-full items-center justify-center">
                  <ArrowRightIcon className="fill-neutral-1000" />
                </div>
              </>
            ) : (
              <div></div>
            )}
            {response && (
              <div className="flex h-full flex-col items-center justify-center overflow-auto p-10">
                <h3 className="text-left text-2xl font-bold">
                  Suggested Change
                </h3>
                <div className="mt-2 whitespace-pre-wrap break-words text-left text-veridianSuccess-400">
                  {extractResponseToShow(response)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className="relative flex flex-1 flex-col items-center justify-center"
        style={{ height: contentHeight }}>
        {data?.content?.mapFeatureCandidate ? (
          <div>
            <DisplayMapFeatureCandidateContent content={data?.content} />
          </div>
        ) : lat || lon ? (
          <div className="flex w-full">
            <div className="h-full w-1/2">
              <ImageCarousel
                key={data?.content?.leaseId}
                images={images}
                SVGOverlay={SVGOverlay}
                detections={detections}
                onImageLoaded={setImageHeight}
                onSelectedImageChange={setSelectedImageIndex}
                startWithIndex="largestDetection"
              />
            </div>
            {imageHeight ? (
              <div
                className="relative w-1/2"
                style={{
                  height: imageHeight,
                }}>
                <DisplayOnMap
                  target={{ lat, lon, azimuth }}
                  expectedTarget={expectedTarget}
                  points={points}
                  options={{
                    hasAppleMaps,
                    ...options,
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex size-full flex-col items-center justify-center">
            {label && <Markdown text={label} />}
            {detections && shouldCropImage ? (
              <ImageCropper
                key={image}
                limitWidth={240}
                limitHeight={280}
                imageSrc={image}
                boundingBox={currentDetection || []}
              />
            ) : (
              <ImageCarousel
                key={data?.content?.leaseId}
                images={images}
                detections={detections}
                SVGOverlay={SVGOverlay}
                onImageLoaded={setImageHeight}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
